/**
 * P1 walk-cycle bob — pure math, no Three.js / R3F imports so it can
 * unit-test cleanly. Consumed from `ArchetypeMech`'s useFrame loop.
 *
 * The voxel mech tweens its base position toward `target` each frame.
 * This helper layers a small Y-axis bob + Z-axis tilt on top, with
 * amplitude proportional to the remaining tween distance so the chassis
 * settles to zero motion when it sits still.
 *
 * Sin of wall-clock is cosmetic-only and never feeds deterministic state.
 */

export const WALK_PERIOD_MS = 320;
export const WALK_BOB_AMPLITUDE = 0.06;
export const WALK_TILT_AMPLITUDE = 0.04;
/**
 * Distance (world units) at which bob/tilt reach full amplitude. One
 * hex is `HEX_SIZE = 1`; this keeps the bob proportional during shorter
 * in-tile micro-adjustments while feeling full on real hex steps.
 */
export const WALK_FULL_AMPLITUDE_DISTANCE = 0.4;

/**
 * Walk-cycle offsets for a mech currently tweening to `(dx, dz)` from
 * its base position. Returns `{ bobY, tiltZ }` to be added on top of
 * the tweened base.
 *
 *   - `bobY` peaks at `WALK_BOB_AMPLITUDE` mid-step, tapers to zero
 *     as remaining distance falls below `WALK_FULL_AMPLITUDE_DISTANCE`.
 *   - `tiltZ` oscillates a quarter-phase ahead for a small side-to-side
 *     sway. Same amplitude scaling.
 */
export function walkOffset(
  dx: number,
  dz: number,
  nowMs: number,
): { bobY: number; tiltZ: number } {
  const dist = Math.hypot(dx, dz);
  const walkFactor = Math.min(1, dist / WALK_FULL_AMPLITUDE_DISTANCE);
  if (walkFactor === 0) return { bobY: 0, tiltZ: 0 };
  const phase = (nowMs / WALK_PERIOD_MS) * Math.PI * 2;
  return {
    bobY: Math.sin(phase) * WALK_BOB_AMPLITUDE * walkFactor,
    tiltZ: Math.cos(phase) * WALK_TILT_AMPLITUDE * walkFactor,
  };
}
